import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { RouteComponentProps } from 'react-router-dom';
import axios from 'axios';
import classnames from 'classnames';

import {
  nextQuietWait,
  readSharePosition,
  receivedAccessibleName,
  SHARE_LOCATION_COPY,
  SHARE_LOCATION_QUIET_RETRY_MS,
  shareLocationOutsideHeading,
  shareLocationSmsHref,
  sleep,
} from './shareLocationFlow';

import '../../../components/Button/style.scss';
import './StaffTextShareLocation.scss';

type ShareView = 'loading' | 'asking' | 'received' | 'unavailable' | 'movedOn' | 'outside' | 'used';
type IconKind = 'asking' | 'received' | 'warning' | 'outside' | 'expired';

interface ShareLocationRouteParams {
  token: string;
}

interface ShareLocationResponse {
  status?: 'OPEN' | 'USED' | 'EXPIRED';
  agencyName?: string;
  textNumber?: string;
}

interface ShareLocationResult {
  result?: 'ACCEPTED' | 'MOVED_ON' | 'OUTSIDE_AGENCY' | 'QUIET';
  reasked?: boolean;
  matchedAddress?: string;
  retryAfterSeconds?: number;
}

interface SharedPoint {
  lat: number;
  long: number;
  accuracyM: number;
}

function apiUrl(path: string): string {
  const baseUrl = process.env.API_URL;
  if (!baseUrl) {
    throw new Error('API_URL is not configured');
  }

  return `${baseUrl.replace(/\/$/, '')}/${path}`;
}

function responseStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

function StatusIcon({ kind }: { kind: IconKind }) {
  return (
    <div className={`share-location__icon share-location__icon--${kind}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" width="32" height="32" focusable="false">
        {kind === 'received' && <path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="currentColor" strokeWidth="2" />}
        {kind === 'warning' && <path d="M12 4l9 16H3L12 4z" fill="none" stroke="currentColor" strokeWidth="2" />}
        {kind === 'expired' && (
          <path d="M12 7v6l4 2M12 3a9 9 0 110 18 9 9 0 010-18z" fill="none" stroke="currentColor" strokeWidth="2" />
        )}
        {(kind === 'asking' || kind === 'outside') && (
          <path
            d="M12 21s6-5.4 6-9.5a6 6 0 10-12 0C6 15.6 12 21 12 21z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
        )}
        {kind === 'outside' && <path d="M9 9l6 6M15 9l-6 6" fill="none" stroke="currentColor" strokeWidth="2" />}
      </svg>
    </div>
  );
}

function headingFor(view: ShareView, agencyName?: string): string {
  if (view === 'asking') {
    return SHARE_LOCATION_COPY.askingHeading;
  }
  if (view === 'received') {
    return SHARE_LOCATION_COPY.receivedHeading;
  }
  if (view === 'unavailable') {
    return SHARE_LOCATION_COPY.couldntGetHeading;
  }
  if (view === 'movedOn') {
    return SHARE_LOCATION_COPY.couldntSaveHeading;
  }
  if (view === 'outside') {
    return shareLocationOutsideHeading(agencyName);
  }
  if (view === 'used') {
    return SHARE_LOCATION_COPY.expiredHeading;
  }

  return SHARE_LOCATION_COPY.product;
}

function iconFor(view: ShareView): IconKind | null {
  if (view === 'asking') {
    return 'asking';
  }
  if (view === 'received') {
    return 'received';
  }
  if (view === 'unavailable' || view === 'movedOn') {
    return 'warning';
  }
  if (view === 'outside') {
    return 'outside';
  }
  if (view === 'used') {
    return 'expired';
  }

  return null;
}

export default function StaffTextShareLocation({ match }: RouteComponentProps<ShareLocationRouteParams>) {
  const token = match.params.token;
  const generation = useRef(0);
  const linkOpened = useRef(false);
  const busyRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [loadId, setLoadId] = useState(0);
  const [view, setView] = useState<ShareView>('loading');
  const [agencyName, setAgencyName] = useState<string | undefined>();
  const [textNumber, setTextNumber] = useState<string | undefined>();
  const [matchedAddress, setMatchedAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const markBusy = useCallback((value: boolean) => {
    busyRef.current = value;
    setBusy(value);
  }, []);

  const postPoint = useCallback(
    async (point: SharedPoint, id: number) => {
      setSaving(true);
      let elapsed = 0;
      try {
        for (;;) {
          if (generation.current !== id) {
            return;
          }
          const response = await axios.post<ShareLocationResult>(
            apiUrl(`public/staff-text/location/${encodeURIComponent(token)}`),
            point
          );
          if (generation.current !== id) {
            return;
          }
          const result = response.data?.result;
          if (result === 'QUIET') {
            const wait = nextQuietWait(elapsed, SHARE_LOCATION_QUIET_RETRY_MS, response.data?.retryAfterSeconds ?? 0);
            if (wait === null) {
              setView('unavailable');

              return;
            }
            await sleep(wait);
            elapsed += wait;
            continue;
          }
          if (result === 'ACCEPTED') {
            setMatchedAddress(response.data?.matchedAddress || '');
            setView('received');

            return;
          }
          if (result === 'MOVED_ON') {
            setView('movedOn');

            return;
          }
          if (result === 'OUTSIDE_AGENCY' && response.data?.reasked) {
            setView('outside');

            return;
          }
          setView('unavailable');

          return;
        }
      } catch (error) {
        if (generation.current !== id || axios.isCancel(error)) {
          return;
        }
        setView(responseStatus(error) === 410 ? 'used' : 'unavailable');
      } finally {
        if (generation.current === id) {
          setSaving(false);
          markBusy(false);
        }
      }
    },
    [token, markBusy]
  );

  const capture = useCallback(
    async (fromUserGesture: boolean) => {
      if (busyRef.current) {
        return;
      }
      const id = generation.current;
      markBusy(true);
      setView('asking');
      const result = await readSharePosition(fromUserGesture);
      if (generation.current !== id) {
        return;
      }
      if (result.ok === false) {
        markBusy(false);
        if (result.reason !== 'needs-tap') {
          setView('unavailable');
        }

        return;
      }
      await postPoint({ lat: result.lat, long: result.long, accuracyM: result.accuracyM }, id);
    },
    [postPoint, markBusy]
  );

  useEffect(() => {
    const controller = new AbortController();
    const id = ++generation.current;
    linkOpened.current = false;
    markBusy(false);
    setSaving(false);
    setView('loading');
    axios
      .get<ShareLocationResponse>(apiUrl(`public/staff-text/location/${encodeURIComponent(token)}`), {
        signal: controller.signal,
      })
      .then((response) => {
        if (generation.current !== id) {
          return;
        }
        setAgencyName(response.data?.agencyName);
        setTextNumber(response.data?.textNumber);
        if (response.data?.status === 'OPEN') {
          linkOpened.current = true;
          void capture(false);

          return;
        }
        setView('used');
      })
      .catch((error) => {
        if (generation.current !== id || axios.isCancel(error)) {
          return;
        }
        setView('unavailable');
      });

    return () => {
      generation.current += 1;
      controller.abort();
    };
  }, [token, loadId, capture]);

  useEffect(() => {
    if (view === 'loading' || view === 'asking') {
      return;
    }
    headingRef.current?.focus();
  }, [view]);

  const smsHref = shareLocationSmsHref(textNumber);
  const icon = iconFor(view);
  const live =
    view === 'loading'
      ? SHARE_LOCATION_COPY.loading
      : view === 'asking' && saving
      ? SHARE_LOCATION_COPY.sharing
      : view === 'received'
      ? receivedAccessibleName(matchedAddress)
      : view === 'unavailable'
      ? SHARE_LOCATION_COPY.couldntGetBody
      : view === 'movedOn'
      ? SHARE_LOCATION_COPY.couldntSaveBody
      : view === 'outside'
      ? SHARE_LOCATION_COPY.outsideBody
      : view === 'used'
      ? SHARE_LOCATION_COPY.expiredBody
      : '';

  return (
    <main className="share-location">
      <div className="share-location__column">
        <header className="share-location__header">
          {agencyName ? <p className="share-location__agency">{agencyName}</p> : null}
          <p className="share-location__product">{SHARE_LOCATION_COPY.product}</p>
        </header>
        {icon ? <StatusIcon kind={icon} /> : null}
        <h1 className="share-location__heading" tabIndex={-1} ref={headingRef}>
          {headingFor(view, agencyName)}
        </h1>
        {view === 'asking' && (
          <>
            <p className="share-location__body">{SHARE_LOCATION_COPY.askingBody}</p>
            <p className="share-location__secondary">{SHARE_LOCATION_COPY.askingFallback}</p>
          </>
        )}
        {view === 'received' && (
          <div className="share-location__card" role="group" aria-label={receivedAccessibleName(matchedAddress)}>
            <p>{SHARE_LOCATION_COPY.receivedLabel}</p>
            <p>{matchedAddress}</p>
          </div>
        )}
        {(view !== 'used' || live) && (
          <div
            role="status"
            aria-live="polite"
            aria-label={live || undefined}
            className={classnames('share-location__body', {
              'share-location__sr-only': view === 'received' || (view === 'asking' && saving),
            })}
          >
            {live}
          </div>
        )}
        <div className="share-location__actions">
          {view === 'asking' && (
            <Button
              variant="primary"
              className="square-btn share-location__button"
              onClick={() => void capture(true)}
              disabled={busy}
            >
              {SHARE_LOCATION_COPY.shareButton}
            </Button>
          )}
          {view === 'unavailable' && (
            <Button
              variant="primary"
              className="square-btn share-location__button share-location__button--outline"
              onClick={() => {
                if (linkOpened.current) {
                  void capture(true);

                  return;
                }
                setLoadId((current) => current + 1);
              }}
            >
              {SHARE_LOCATION_COPY.tryAgain}
            </Button>
          )}
          {view !== 'loading' && view !== 'asking' && smsHref ? (
            <a className="btn square-btn btn-primary share-location__button" href={smsHref}>
              {SHARE_LOCATION_COPY.backToMessages}
            </a>
          ) : null}
        </div>
      </div>
    </main>
  );
}
